import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';

import Helpers, { MAX_ATTEMPTS, getTodayKey, normalizeToken, tokenizeWord } from './components/HelpersFunctions'
import Keyboard from './components/Keyboard';
import Grid from './components/Grid';
import ResultOverlay from './components/ResultOverlay';
import HowToPlay from './components/HowToPlay';
import Stats from './components/Stats';

const WORD_API_URL = process.env.REACT_APP_WORD_API_URL || 'https://api.wodu.app/word'

function getUserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

function normalizeWordOfDayPayload(data) {
  const source = typeof data?.word === 'string' ? data : data?.word
  const normalized = {
    word: source?.word,
    meaning: source?.meaning,
    riddle: source?.riddle ?? data?.riddle ?? null,
    date_key: data?.date_key || source?.date_key,
    time_zone: data?.time_zone || source?.time_zone,
  }

  if (
    typeof normalized.word !== 'string' ||
    typeof normalized.meaning !== 'string' ||
    typeof normalized.date_key !== 'string'
  ) {
    throw new Error('Unexpected API shape')
  }

  return normalized
}

async function fetchWordOfDay() {
  const timeZone = getUserTimeZone()
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem('wordOfDay')) } catch { return null }
  })()

  if (cached) {
    try {
      const normalizedCached = normalizeWordOfDayPayload(cached)
      if (normalizedCached.date_key === getTodayKey() && normalizedCached.time_zone === timeZone) {
        return normalizedCached
      }
    } catch {
      localStorage.removeItem('wordOfDay')
    }
  }

  const url = new URL(WORD_API_URL, window.location.origin)
  url.searchParams.set('tz', timeZone)

  const res = await fetch(url.toString(), { cache: 'no-store' })
  if (!res.ok) throw new Error(`Word API error: ${res.status}`)
  const data = normalizeWordOfDayPayload(await res.json())
  localStorage.setItem('wordOfDay', JSON.stringify(data))
  return data
}

function App() {
  const [wordData, setWordData] = useState(null)
  const [fetchError, setFetchError] = useState(false)

  const loadWordOfDay = useCallback(() => {
    setFetchError(false)
    return fetchWordOfDay()
      .then(setWordData)
      .catch(() => setFetchError(true))
  }, [])

  useEffect(() => {
    loadWordOfDay()
  }, [loadWordOfDay])

  if (fetchError) {
    return (
      <div className="App">
        <div className="full-grid-wrap" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <p style={{ fontFamily: 'var(--font-text)', color: 'var(--ink-mute)', fontSize: 14 }}>
            Could not load today's word. Please check your connection and refresh.
          </p>
        </div>
      </div>
    )
  }

  if (!wordData) {
    return (
      <div className="App">
        <div className="full-grid-wrap" />
      </div>
    )
  }

  return <Game key={`${wordData.date_key}:${wordData.time_zone}`} wordData={wordData} refreshWordOfDay={loadWordOfDay} />
}

function Game({ wordData, refreshWordOfDay }) {
  const answerTokens = useMemo(() => tokenizeWord(wordData.word), [wordData.word])
  const wordLength = answerTokens.length
  const [bootstrapState] = useState(() => Helpers.initializeGameState(wordLength))

  const [answerDictionaryState, setAnswerDictionaryState] = useState(() => bootstrapState.answerDictionary)
  const [currentAttKey, setCurrentAttKey] = useState(() => bootstrapState.currentAtt)
  const [currentIndex, setCurrentIndex] = useState(() => bootstrapState.currentIndex)
  const [attemptDict, setAttemptDict] = useState(() => bootstrapState.attemptDict)
  const [gameStatus, setGameStatus] = useState(() => bootstrapState.gameStatus)
  const [showHowToPlay, setShowHowToPlay] = useState(() => !localStorage.getItem('seenHowToPlay'))
  const [showStats, setShowStats] = useState(false)
  const [showResultOverlay, setShowResultOverlay] = useState(() => bootstrapState.gameStatus !== "IN_PROGRESS")
  const gameOver = gameStatus === "WIN" || gameStatus === "LOSE"

  useEffect(() => {
    if (Helpers.nextDay()) {
      refreshWordOfDay()
    }
  }, [refreshWordOfDay, wordLength])

  const syncDayAndMaybeReset = () => {
    if (!Helpers.nextDay()) return false
    refreshWordOfDay()
    return true
  }

  const updateGameStatus = (nextStatus) => {
    setGameStatus(nextStatus)
    setShowResultOverlay(nextStatus === "WIN" || nextStatus === "LOSE")
    localStorage.setItem("gameStatus", nextStatus)
    localStorage.setItem("lastTimePlayed", String(Date.now()))
  }

  const buttonClick = (element) => {
    if (syncDayAndMaybeReset()) return
    if (gameStatus !== "IN_PROGRESS") return

    const currentRow = attemptDict[currentAttKey]
    if (!Array.isArray(currentRow)) return

    if (element === 'enter') {
      const answerArr = Helpers.answerCheck(
        currentAttKey,
        answerTokens,
        setAnswerDictionaryState,
        setCurrentAttKey,
        setCurrentIndex
      )
      if (!answerArr) return

      const correct = Helpers.correctCheck(answerArr, wordLength)
      if (correct) {
        const attemptCount = Object.values(answerDictionaryState).filter(Boolean).length + 1
        Helpers.recordResult(true, attemptCount)
        updateGameStatus("WIN")
        localStorage.setItem("lastCompleted", String(Date.now()))
        return
      }

      if (currentAttKey >= MAX_ATTEMPTS) {
        Helpers.recordResult(false, MAX_ATTEMPTS)
        updateGameStatus("LOSE")
        return
      }

      updateGameStatus("IN_PROGRESS")
      return
    }

    if (element === '<') {
      Helpers.deleteCharacter(currentAttKey, currentIndex, setCurrentIndex, setAttemptDict)
      return
    }

    if (Helpers.rowComplete(currentAttKey) || currentIndex >= wordLength) return

    const tempAttemptDict = { ...attemptDict }
    const currentAttArr = [...tempAttemptDict[currentAttKey]]
    currentAttArr[currentIndex] = normalizeToken(element)
    tempAttemptDict[currentAttKey] = currentAttArr
    const nextIndex = Math.min(currentIndex + 1, wordLength)
    setCurrentIndex(nextIndex)
    setAttemptDict(tempAttemptDict)
    localStorage.setItem("currentIndex", JSON.stringify(nextIndex))
    localStorage.setItem("attemptDict", JSON.stringify(tempAttemptDict))
    localStorage.setItem("lastTimePlayed", String(Date.now()))
  }

  useEffect(() => {
    if (!localStorage.getItem("lastTimePlayed")) {
      localStorage.setItem("lastTimePlayed", String(Date.now()))
    }
  }, [])

  return (
    <div className="App">
      <header className="wodu-topbar">
        <button className="wodu-iconbtn" onClick={() => setShowHowToPlay(true)} aria-label="How to play">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" strokeLinecap="round" />
            <circle cx="12" cy="17" r="0.6" fill="currentColor" />
          </svg>
        </button>
        <div className="wodu-topbar__center">
          <span className="wodu-wordmark">Wọ́dù</span>
          <span className="wodu-tagline">Yorùbá Wordle</span>
        </div>
        <button className="wodu-iconbtn" onClick={() => setShowStats(true)} aria-label="Statistics">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="4" y="13" width="4" height="7" rx="1" />
            <rect x="10" y="8" width="4" height="12" rx="1" />
            <rect x="16" y="4" width="4" height="16" rx="1" />
          </svg>
        </button>
      </header>
      {showHowToPlay && (
        <HowToPlay onClose={() => {
          localStorage.setItem('seenHowToPlay', '1')
          setShowHowToPlay(false)
        }} />
      )}
      {showStats && <Stats onClose={() => setShowStats(false)} />}
      <div className="full-grid-wrap">
        {wordData.riddle && (
          <RiddleBanner
            riddle={wordData.riddle}
            failedAttempts={Object.values(answerDictionaryState).filter(Boolean).length}
          />
        )}
        <Grid
          attemptDict={attemptDict}
          answerDictionaryState={answerDictionaryState}
        />
        <div className="keyboard-stage">
          <Keyboard
            buttonClick={buttonClick}
            answerDictionaryState={answerDictionaryState}
            attemptDict={attemptDict}
            gameStatus={gameStatus}
          />
          {gameOver && !showResultOverlay && (
            <button
              className="keyboard-share"
              onClick={() => setShowResultOverlay(true)}
              aria-label="Show share results"
            >
              See Result
            </button>
          )}
        </div>
        {showResultOverlay && gameOver && (
          <ResultOverlay
            won={gameStatus === 'WIN'}
            answerDictionaryState={answerDictionaryState}
            word={wordData.word}
            attemptCount={Object.values(answerDictionaryState).filter(Boolean).length}
            onClose={() => setShowResultOverlay(false)}
          />
        )}
      </div>
    </div>
  )
}

const HINT_UNLOCK_AT = 3

function RiddleBanner({ riddle, failedAttempts }) {
  const unlocked = failedAttempts >= HINT_UNLOCK_AT
  const remaining = HINT_UNLOCK_AT - failedAttempts

  if (unlocked) {
    return (
      <div className="wodu-clue wodu-clue--unlocked">
        <div className="wodu-clue__badge">Ìtàjì</div>
        <p className="wodu-clue__text">"{riddle}"</p>
      </div>
    )
  }

  return (
    <div className="wodu-clue wodu-clue--locked">
      <div className="wodu-clue__badge wodu-clue__badge--locked">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div className="wodu-clue__locked-body">
        <p className="wodu-clue__locked-yo">
          Ìtàjì yíò hàn lẹ́yìn ìgbà ({remaining}) àdánwò
        </p>
        <p className="wodu-clue__locked-en">
          Hint unlocks after {remaining} more {remaining === 1 ? 'attempt' : 'attempts'}
        </p>
      </div>
    </div>
  )
}

export default App;
